import React, { useCallback } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { MediaInsertTabProps } from '@atlaskit/editor-plugin-media-insert';
import { ImageFromAIGeneration } from '@atlassian/ai-image-generation/image-from-ai-generation';

/**
 * Adapter that renders `<ImageFromAIGeneration>` (a private
 * `@atlassian/ai-image-generation` component) inside the public media insert
 * picker as a registered tab.
 *
 * Lives in this private package (which already depends on the private
 * AI image-generation package) so that `@atlaskit/editor-plugin-media-insert`
 * never has to import any `@atlassian/*` package.
 *
 * Wired in via `api.mediaInsert.actions.registerInsertTab(...)` from
 * `aiImageGenerationPlugin`'s setup.
 */
export function MediaInsertImageGenerationTab({
	closeMediaInsertPicker,
	insertMediaSingle,
	mediaProvider,
}: MediaInsertTabProps): React.JSX.Element {
	const insertMedia = useCallback(
		({
			id,
			collection,
			fileMimeType,
			dimensions,
		}: {
			collection: string | undefined;
			dimensions?: { height: number; width: number };
			fileMimeType?: string;
			id: string | undefined;
		}) => {
			if (!id) {
				return;
			}
			insertMediaSingle({
				inputMethod: INPUT_METHOD.MEDIA_PICKER,
				mediaState: {
					id,
					collection,
					fileMimeType,
					dimensions,
				},
			});
		},
		[insertMediaSingle],
	);

	return (
		<ImageFromAIGeneration
			mediaUploadCollection={mediaProvider.uploadParams?.collection}
			insertMedia={insertMedia}
			closePopup={closeMediaInsertPicker}
		/>
	);
}
